import { Paper, rem } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { useParams, useRouter, useBlocker } from '@tanstack/react-router';
import { flatten } from '@turf/turf';
import { Feature, FeatureCollection, MultiPolygon, Polygon } from 'geojson';
import { Map, MapEvent } from 'mapbox-gl';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MapRef } from 'react-map-gl';

import FieldForm, {
  FieldFormProps,
} from '../../components/field-form/field-form';
import FieldsInBounds from '../../components/fields-in-bounds/fields-in-bounds';
import {
  FieldFormProvider,
  useFieldForm,
} from '../../hooks/use-field-form/field-form';

import { ErrorPage } from './page.error';

import { useQueriesWithGlobal } from '@data-access';
import {
  fieldsQueries,
  hasFieldGeometry,
  useCreateField,
  useUpdateField,
} from '@fields/data-access';
import {
  DrawToolboxControl,
  GeocoderControl,
  hasZoomableGeometry,
  PfMap,
  StyleControl,
  zoomOnFeature,
} from '@map';
import { FullMainHeight } from '@ui';

export function ManageFieldPage() {
  const { t } = useTranslation();
  const { fieldId } = useParams({ strict: false });
  const { navigate } = useRouter();
  const [pageType] = useState<'edit' | 'create'>(fieldId ? 'edit' : 'create');
  const mapRef = useRef<MapRef>(null);
  const form = useFieldForm();
  useBlocker({
    blockerFn: () => window.confirm(t('common.confirm_navigation')),
    condition: form.isDirty(),
  });

  const queries = useQueriesWithGlobal(fieldsQueries);
  const field = useQuery({
    ...queries.detail(Number(fieldId)),
    enabled: !!fieldId,
  });
  const maxBounds = useQuery(queries.maxBounds());
  const createField = useCreateField();
  const updateField = useUpdateField();

  const [initialPolygon, setInitialPolygon] = useState(
    field.data?.geometry ? flatten(field.data as GeoJSON.Feature) : undefined,
  );

  const fitMapView = useCallback(
    (map?: Map) => {
      if (!map) {
        return;
      }

      if (field.data && hasZoomableGeometry(field.data)) {
        zoomOnFeature({
          map,
          geojson: field.data,
        });
        return;
      }

      if (maxBounds.data?.bbox) {
        map.fitBounds(maxBounds.data.bbox, { padding: 20 });
      }
    },
    [field.data, maxBounds.data],
  );

  const navigateToField = (id: number) =>
    navigate({
      to: `/fields/$fieldId`,
      params: { fieldId: id.toString() },
    });

  const handleLoad = (map: MapEvent) => {
    fitMapView(map.target);
  };

  const handleDrawEnd = (features: Feature<Polygon>[]) => {
    form.setFieldValue('boundaries', features);
  };

  const handleSubmit: FieldFormProps['onSubmit'] = ({ type, values }) => {
    if (type === 'create') {
      createField.mutate(values, {
        onSuccess: () => {
          form.resetDirty();
        },
      });
    }

    if (type === 'edit' && fieldId) {
      const sanitizedValues = Object.fromEntries(
        Object.entries(values).map(([key, value]) => [
          key,
          value === undefined ? null : value,
        ]),
      );

      const { is_multiple, ...data } = sanitizedValues;

      console.log('data', data);

      updateField.mutate(
        {
          id: Number(fieldId),
          ...data,
        },
        {
          onSuccess: () => {
            form.resetDirty();
          },
        },
      );
    }
  };

  useEffect(() => {
    if (field.data) {
      fitMapView(mapRef?.current?.getMap());
      setInitialPolygon(
        field.data.geometry
          ? flatten(field.data as GeoJSON.Feature)
          : undefined,
      );

      form.setInitialValues({
        name: field.data.properties.name,
        farm: field.data.properties.farm.id.toString(),
        species: field.data.properties.species?.id.toString(),
        variety: field.data.properties.variety?.id.toString(),
      });
      form.reset();
      form.resetDirty();
    }
  }, [field.data, fitMapView]);

  useEffect(() => {
    if (maxBounds.isSuccess) {
      fitMapView(mapRef.current?.getMap());
    }
  }, [fitMapView, maxBounds.isSuccess]);

  useEffect(() => {
    if (updateField.isSuccess) {
      navigateToField(updateField.data.id);
    }
  }, [updateField]);

  useEffect(() => {
    if (createField.isSuccess) {
      if (!Array.isArray(createField.data)) {
        navigateToField(createField.data.id);
      } else {
        navigate({ to: '/fields' });
      }
    }
  }, [createField]);

  return (
    <FullMainHeight.Root>
      <FullMainHeight.FlexContent>
        <FullMainHeight.Sidebar>
          <Paper flex="1 0 auto" h="100%" maw={rem(400)}>
            <FieldFormProvider form={form}>
              <FieldForm
                loading={createField.isPending || updateField.isPending}
                onSubmit={handleSubmit}
                type={pageType}
              />
            </FieldFormProvider>
          </Paper>
        </FullMainHeight.Sidebar>
        <Paper flex="1 1 auto" h="100%" p={0}>
          <PfMap onLoad={handleLoad} ref={mapRef}>
            <FieldsInBounds
              fieldId={fieldId}
              hideClusters
              withSatelliteControl={pageType === 'edit'}
              withWmsLayer
            />
            <StyleControl />
            <DrawToolboxControl
              guidanceControls={['draw', 'edit', 'cut', 'delete_point']}
              initialPolygon={initialPolygon}
              onDrawEnd={handleDrawEnd}
            />
            <GeocoderControl />
          </PfMap>
        </Paper>
      </FullMainHeight.FlexContent>
    </FullMainHeight.Root>
  );
}

ManageFieldPage.Error = ErrorPage;

export default ManageFieldPage;
